import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppShell } from "@/components/AppShell";
import { DateInput } from "@/components/DateInput";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Plus, Trash2, Upload } from "lucide-react";
import { Link } from "react-router-dom";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);
const n = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// spreadsheet columns: [field, label, type, width]
const COLS = [
  ["date", "Date", "date", "w-[120px]"],
  ["supplier", "Supplier / Mill", "text", "w-[150px]"],
  ["po_no", "PO No.", "text", "w-[100px]"],
  ["company", "Company", "text", "w-[140px]"],
  ["item", "Item / Quality", "text", "w-[170px]"],
  ["shade", "Shade", "text", "w-[80px]"],
  ["qty", "Qty Ordered", "number", "w-[100px]"],
  ["rate", "Rate", "number", "w-[90px]"],
  ["dispatched_qty", "Dispatched", "number", "w-[100px]"],
  ["dispatch_date", "Disp. Date", "date", "w-[120px]"],
  ["lr_no", "LR No.", "text", "w-[100px]"],
  ["bale_no", "Bale No.", "text", "w-[100px]"],
  ["notes", "Notes", "text", "w-[140px]"],
];
const FIELD_LABEL = Object.fromEntries(COLS.map(([f, l]) => [f, l]));
const blank = () => ({ id: null, ...Object.fromEntries(COLS.map(([f, , t]) => [f, t === "number" ? "" : ""])), dirty: true });

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  part: "bg-sky-50 text-sky-700 border-sky-200",
  dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const VIEW_META = {
  all: {
    title: "Order Sheet",
    blurb: "Place orders on mills — type or paste rows and they save automatically. Rows stay here for good.",
    testId: "mill-view-all",
  },
  pending: {
    title: "Pending Orders",
    blurb: "Orders the mill has not dispatched yet. Fill Bale No., LR No. and Dispatch Date and the row moves to Dispatch.",
    testId: "mill-view-pending",
  },
  dispatch: {
    title: "Dispatch",
    blurb: "Orders dispatched by the mill — Bale No., LR No. and Dispatch Date are filled in. Still editable for corrections.",
    testId: "mill-view-dispatch",
  },
};

export default function MillOrders({ view = "all" }) {
  const meta = VIEW_META[view];
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [file, setFile] = useState(null);
  const gridRef = useRef(null);
  const saveTimer = useRef(null);
  const rowsRef = useRef([]);
  rowsRef.current = rows;

  const params = () => ({ search, supplier, status, view });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/po-sheet", { params: params() });
      setRows(view === "all" ? [...data.rows.map((r) => ({ ...r, dirty: false })), blank()]
                             : data.rows.map((r) => ({ ...r, dirty: false })));
      setTotals(data.totals);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/supplier").then(({ data }) => setSuppliers(data.map((m) => m.name))).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const derived = useMemo(
    () =>
      rows.map((r) => {
        const qty = parseFloat(r.qty) || 0;
        const done = parseFloat(r.dispatched_qty) || 0;
        const balance = Math.round((qty - done) * 100) / 100;
        const dispatched = ["bale_no", "lr_no", "dispatch_date"].every((f) => String(r[f] ?? "").trim());
        return {
          ...r,
          balance,
          statusKey: dispatched ? (balance > 0 ? "part" : "dispatched") : "pending",
          pendingValue: Math.round(balance * (parseFloat(r.rate) || 0) * 100) / 100,
        };
      }),
    [rows]
  );

  const setCell = (i, field, value) => {
    setRows((rs) => {
      const next = rs.map((r, idx) => (idx === i ? { ...r, [field]: value, dirty: true } : r));
      return i === rs.length - 1 ? [...next, blank()] : next;
    });
    queueSave();
  };

  // autosave: every edit is written a moment after you stop typing
  const queueSave = () => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(), 900);
  };

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const keyNav = (e) => {
    if (!["Enter", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Backspace"].includes(e.key)) return;
    const cells = Array.from(gridRef.current?.querySelectorAll("input[data-cell]") || []);
    const at = cells.indexOf(e.target);
    if (at === -1) return;
    // backspace on an already empty cell steps back to the previous cell
    if (e.key === "Backspace") {
      if (e.target.value) return;
      e.preventDefault();
      cells[at - 1]?.focus();
      return;
    }
    // left/right hop cells only from the edge of the text, so the caret still works inside a cell
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const el = e.target;
      const caret = el.selectionStart ?? 0;
      const atStart = caret === 0 && el.selectionEnd === 0;
      const atEnd = caret === (el.value?.length || 0) && el.selectionEnd === caret;
      if (e.key === "ArrowLeft" && !atStart) return;
      if (e.key === "ArrowRight" && !atEnd) return;
      e.preventDefault();
      cells[at + (e.key === "ArrowRight" ? 1 : -1)]?.focus();
      return;
    }
    e.preventDefault();
    if (e.key === "Enter") {
      const next = cells[at + 1];
      if (next) next.focus();
      else setRows((rs) => [...rs, blank()]);
      return;
    }
    const step = (e.key === "ArrowDown" ? 1 : -1) * COLS.length;
    cells[at + step]?.focus();
  };

  // paste a block copied from Excel straight into the grid
  const onPaste = (e, rowIndex, colIndex) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text || !/[\t\n]/.test(text)) return;
    e.preventDefault();
    const block = text.replace(/\r/g, "").split("\n").filter((l) => l.length).map((l) => l.split("\t"));
    setRows((rs) => {
      const next = [...rs];
      block.forEach((line, r) => {
        const target = rowIndex + r;
        if (!next[target]) next[target] = blank();
        line.forEach((value, c) => {
          const col = COLS[colIndex + c];
          if (!col) return;
          next[target] = { ...next[target], [col[0]]: value.trim(), dirty: true };
        });
      });
      if (next[next.length - 1]?.item || next[next.length - 1]?.po_no) next.push(blank());
      return next;
    });
    toast.success(`Pasted ${block.length} row(s)`);
    queueSave();
  };

  const save = async () => {
    const dirty = rowsRef.current.filter(
      (r) => r.dirty && COLS.some(([f]) => String(r[f] ?? "").trim() !== "")
    );
    if (dirty.length === 0) return;
    setSaveState("saving");
    try {
      await api.post("/po-sheet/bulk", {
        rows: dirty.map((r) => ({
          id: r.id,
          ...Object.fromEntries(
            COLS.map(([f, , t]) => [f, t === "number" ? parseFloat(r[f]) || 0 : String(r[f] ?? "")])
          ),
        })),
      });
      setSaveState("saved");
      load();
    } catch (err) {
      setSaveState("idle");
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeRow = async (i) => {
    const row = rows[i];
    if (row.id) {
      try {
        await api.delete(`/po-sheet/${row.id}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        return;
      }
    }
    setRows((rs) => rs.filter((_, idx) => idx !== i));
    if (row.id) load();
  };

  const download = async (format) => {
    try {
      const res = await api.get("/po-sheet/export", { params: { ...params(), format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mill-order-sheet.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const template = async () => {
    const res = await api.get("/po-sheet/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mill-order-import-template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const pickFile = async (f) => {
    setFile(f);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/po-sheet/preview", form);
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const runImport = async () => {
    const form = new FormData();
    form.append("file", file);
    form.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/po-sheet/import", form);
      toast.success(`Imported ${data.imported} row(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      setImportOpen(false);
      setPreview(null);
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1600px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Mill Orders</p>
          <h1 data-testid={meta.testId} className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">
            {meta.title}
          </h1>
          <p className="mt-2 text-sm text-zinc-500">{meta.blurb}</p>
          <div className="mt-4 flex flex-wrap gap-1">
            {[
              ["/mill-orders/order", "Order", "mill-tab-order"],
              ["/mill-orders/pending", "Pending", "mill-tab-pending"],
              ["/mill-orders/dispatch", "Dispatch", "mill-tab-dispatch"],
            ].map(([to, label, tid]) => (
              <Link
                key={to}
                to={to}
                data-testid={tid}
                className={`border-b-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                  (view === "all" && label === "Order") ||
                  (view === "pending" && label === "Pending") ||
                  (view === "dispatch" && label === "Dispatch")
                    ? "border-[#C8A03C] text-zinc-950"
                    : "border-transparent text-zinc-500 hover:text-zinc-950"
                }`}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 py-6">
          <div className="w-[190px]">
            <L>Search</L>
            <Input
              data-testid="mill-search-input"
              className="mt-2 rounded-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="PO, item, shade…"
            />
          </div>
          <div className="w-[180px]">
            <L>Mill / Supplier</L>
            <select
              data-testid="mill-supplier-filter"
              className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">All mills</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="w-[150px]">
            <L>Status</L>
            <select
              data-testid="mill-status-filter"
              className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="part">Part dispatched</option>
              <option value="dispatched">Dispatched</option>
            </select>
          </div>
          <Button data-testid="mill-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
            Show
          </Button>
          <span
            data-testid="mill-save-state"
            className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400"
          >
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "All changes saved" : "Autosave on"}
          </span>
          {view === "all" && (
            <Button data-testid="mill-import-button" variant="outline" className="rounded-sm" onClick={() => setImportOpen(true)}>
              <Upload size={14} className="mr-1.5" /> Import Excel
            </Button>
          )}
          <Button data-testid="mill-export-excel" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
            <FileSpreadsheet size={14} className="mr-1.5" /> Excel
          </Button>
          <Button data-testid="mill-export-pdf" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>

        {totals && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="mill-totals">
            {[
              ["Rows", totals.rows],
              ["Pending rows", totals.pending_rows],
              ["Qty ordered", n(totals.qty)],
              ["Dispatched", n(totals.dispatched_qty)],
              ["Balance qty", n(totals.balance)],
              ["Pending value", `₹${n(totals.pending_value)}`],
            ].map(([label, value]) => (
              <div key={label} className="border border-zinc-200 bg-white p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                <p className="font-mono-num mt-1.5 text-lg font-semibold">{value}</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : (
          <div className="mt-6 overflow-x-auto border border-zinc-200 bg-white" ref={gridRef} onKeyDown={keyNav}>
            <table className="w-full border-collapse text-xs" data-testid="mill-grid">
              <thead>
                <tr>
                  <th className="w-[36px] border border-[var(--mod-border)] bg-[var(--mod-head)] px-1 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    #
                  </th>
                  {COLS.map(([f, label, , w]) => (
                    <th
                      key={f}
                      className={`${w} border border-[var(--mod-border)] bg-[var(--mod-head)] px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-500`}
                    >
                      {label}
                    </th>
                  ))}
                  <th className="w-[90px] border border-[var(--mod-border)] bg-[var(--mod-head)] px-2 py-1.5 text-right text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    Balance
                  </th>
                  <th className="w-[110px] border border-[var(--mod-border)] bg-[var(--mod-head)] px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    Status
                  </th>
                  <th className="w-[100px] border border-[var(--mod-border)] bg-[var(--mod-head)] px-2 py-1.5 text-right text-[9px] font-bold uppercase tracking-[0.1em] text-zinc-500">
                    Pending Value
                  </th>
                  <th className="w-[34px] border border-[var(--mod-border)] bg-[var(--mod-head)]" />
                </tr>
              </thead>
              <tbody>
                {derived.map((r, i) => (
                  <tr
                    key={r.id || `new-${i}`}
                    data-testid={`mill-row-${i}`}
                    className={r.statusKey === "dispatched" ? "bg-emerald-50/40" : ""}
                  >
                    <td className="border border-[var(--mod-border)] bg-[var(--mod-head)] px-1 text-center text-[10px] text-[var(--mod-dark)]">{i + 1}</td>
                    {COLS.map(([f, , type], c) => (
                      <td key={f} className="border border-zinc-200 p-0">
                        {type === "date" ? (
                          <DateInput
                            data-cell={f}
                            data-testid={`mill-cell-${i}-${f}`}
                            className="h-7 w-full px-1.5"
                            inputClassName="h-full border-0 bg-transparent p-0 text-xs outline-none"
                            value={r[f] ?? ""}
                            onChange={(v) => setCell(i, f, v)}
                          />
                        ) : (
                        <input
                          data-cell={f}
                          data-testid={`mill-cell-${i}-${f}`}
                          type={type === "number" ? "number" : "text"}
                          step="any"
                          className={`h-7 w-full border-0 bg-transparent px-1.5 text-xs outline-none focus:bg-amber-50 focus:ring-1 focus:ring-inset focus:ring-[#C8A03C] ${
                            type === "number" ? "font-mono-num text-right" : ""
                          }`}
                          value={r[f] ?? ""}
                          onPaste={(e) => onPaste(e, i, c)}
                          onChange={(e) => setCell(i, f, e.target.value)}
                        />
                        )}
                      </td>
                    ))}
                    <td className="font-mono-num border border-zinc-200 px-2 text-right font-semibold">
                      {r.qty ? n(r.balance) : ""}
                    </td>
                    <td className="border border-zinc-200 px-2">
                      {r.qty || r.dispatched_qty ? (
                        <span
                          data-testid={`mill-status-${i}`}
                          className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${STATUS_STYLE[r.statusKey]}`}
                        >
                          {r.statusKey === "part" ? "Part" : r.statusKey}
                        </span>
                      ) : null}
                    </td>
                    <td className="font-mono-num border border-zinc-200 px-2 text-right">
                      {r.qty ? `₹${n(r.pendingValue)}` : ""}
                    </td>
                    <td className="border border-zinc-200 text-center">
                      <button
                        type="button"
                        data-testid={`mill-delete-${i}`}
                        onClick={() => removeRow(i)}
                        className="px-1 text-zinc-400 hover:text-red-700"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-zinc-200 bg-zinc-50 px-3 py-2">
              {view === "all" ? (
                <button
                  type="button"
                  data-testid="mill-add-row"
                  onClick={() => setRows((rs) => [...rs, blank()])}
                  className="flex items-center gap-1 border border-zinc-300 bg-white px-2 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-100"
                >
                  <Plus size={12} /> Add row
                </button>
              ) : (
                <span className="text-[11px] text-zinc-500">
                  {view === "pending" ? "Fill Bale, LR and Dispatch Date to move a row to Dispatch" : "Dispatched rows"}
                </span>
              )}
              <span className="text-[11px] text-zinc-500">
                Enter / ↓ ↑ ← → move between cells · paste a block straight from Excel · changes save automatically
              </span>
            </div>
          </div>
        )}
      </main>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="mill-import-dialog" className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">Import mill orders</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Use our template, or upload any Excel file and match the columns yourself.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-3">
            <Button data-testid="mill-template-button" variant="outline" className="rounded-sm" onClick={template}>
              <Download size={14} className="mr-1.5" /> Download template
            </Button>
            <input
              data-testid="mill-file-input"
              type="file"
              accept=".xlsx,.xlsm"
              className="text-sm"
              onChange={(e) => e.target.files?.[0] && pickFile(e.target.files[0])}
            />
          </div>

          {preview && (
            <div className="mt-2 max-h-[50vh] overflow-auto border border-zinc-200">
              <table className="w-full text-xs" data-testid="mill-import-preview">
                <thead className="sticky top-0 bg-[var(--mod-head)]">
                  <tr>
                    {preview.headers.map((h, i) => (
                      <th key={`${h}-${i}`} className="border border-zinc-200 p-1 text-left align-top">
                        <p className="font-bold uppercase tracking-wide text-zinc-500">{h || `Col ${i + 1}`}</p>
                        <select
                          data-testid={`mill-map-${i}`}
                          className="mt-1 w-full border border-zinc-300 bg-white px-1 py-0.5 text-[11px]"
                          value={mapping[String(i)] || ""}
                          onChange={(e) =>
                            setMapping((m) => {
                              const next = { ...m };
                              if (e.target.value) next[String(i)] = e.target.value;
                              else delete next[String(i)];
                              return next;
                            })
                          }
                        >
                          <option value="">— skip —</option>
                          {preview.fields.map((f) => (
                            <option key={f} value={f}>{FIELD_LABEL[f] || f}</option>
                          ))}
                        </select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i}>
                      {preview.headers.map((_, c) => (
                        <td key={c} className="border border-zinc-200 px-1 py-0.5 whitespace-nowrap">
                          {r[c] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview && (
            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              <p className="text-xs text-zinc-500">
                {preview.total_rows} row(s) in the file · showing first {preview.rows.length}
              </p>
              <Button
                data-testid="mill-import-confirm"
                className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
                onClick={runImport}
              >
                Import {preview.total_rows} row(s)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
